/**
 * NOW panel (#767, story-first IA) — the job page's top card, built from the
 * approved mockup (threads/story-first-ia/mockup-job-page.html, v4/v5).
 *
 * Two labeled rows Corbin asked for, kept separate on purpose:
 *   SUMMARY — the ask (office → tech, forward): what to do + access +
 *   standing instructions. Authored (`jobs.summary`, rendered via the
 *   embedded JobSummaryCard so editing keeps working).
 *   THE STORY — what happened from that ask (job → office, backward): derived
 *   high-point beats ("✓ RPZ passed · ✗ DC failed (relief valve) → $172
 *   invoice"), never typed. Shared derivation: @/lib/job-story.
 *
 * Below the rows: the pinned tech note (amber), then three zones —
 * "What needs you" (every row from deriveJobBlockers + the check-up — the
 * relocated What's Holding rows; the cap of 3 hid the most actionable one,
 * audit 2026-10-01), ONE stage-derived "Next step" primary, and the Money
 * snapshot (Invoice / Estimate / Balance, each scrolling to Paperwork).
 * With nothing blocking, the whole card collapses to one green on-track
 * line (expandable — the ask and story stay one click away).
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
    deriveJobStory,
    deriveMoneyTotals,
    formatMoneyStat,
    OUTCOME_MARK,
    OUTCOME_WORD,
    type JobStory,
} from "@/lib/job-story";
import {
    AlertTriangle,
    ArrowRight,
    CalendarPlus,
    CheckCircle2,
    ChevronDown,
    ChevronUp,
    Gauge,
    Package,
    PauseCircle,
    Pin,
    Send,
} from "lucide-react";
import {
    deriveJobBlockers,
    type BlockerInvoiceLike,
    type BlockerJobLike,
    type BlockerPoLike,
    type BlockerSeverity,
    type JobBlocker,
    blockersFromCheckup,
    nextStepContradicted,
} from "../jobBlockers";
import { openHelpDesk, useJobCheckup } from "@/lib/use-job-checkup";
import type { HeaderAction } from "../jobDetailHelpers";
import type { DeliveredAnotherWay, JobDetail, LinkedEstimate, LinkedInvoice, JobTestResult } from "./types";

// ── The relocated "What's holding this up" severity rows (Issue #301) ──────

const SEVERITY_DOT: Record<BlockerSeverity, string> = {
    blocked: "bg-red-500",
    attention: "bg-amber-500",
    info: "bg-sky-500",
};

function BlockerIcon({ blocker, className }: { blocker: JobBlocker; className?: string }) {
    const Icon =
        blocker.id === "parts"
            ? Package
            : blocker.id === "held"
              ? PauseCircle
              : blocker.id === "unsent_invoice"
                ? Send
                : blocker.id === "unscheduled" || blocker.id === "scheduled_after_due"
                  ? CalendarPlus
                  : blocker.id === "no_devices"
                    ? Gauge
                    : AlertTriangle;
    return <Icon className={className} aria-hidden />;
}

function BlockerActionButton({
    blocker,
    onSendInvoice,
    onSetSchedule,
    onAddDevices,
    onResendMissed,
}: {
    blocker: JobBlocker;
    onSendInvoice?: (invoiceId: string) => void;
    onSetSchedule?: () => void;
    onAddDevices?: () => void;
    onResendMissed?: () => void;
}) {
    const action = blocker.action;
    if (!action) return null;

    if (action.kind === "po") {
        return (
            <Button asChild size="sm" variant="outline" className="h-7 shrink-0 bg-transparent text-xs">
                <Link to={`/purchasing?po=${action.poId}`}>
                    {action.label}
                    <ArrowRight className="ml-1 h-3.5 w-3.5" />
                </Link>
            </Button>
        );
    }
    if (action.kind === "invoice") {
        if (!onSendInvoice) return null;
        return (
            <Button
                size="sm"
                variant="outline"
                className="h-7 shrink-0 bg-transparent text-xs"
                onClick={() => onSendInvoice(action.invoiceId)}
            >
                {action.label}
            </Button>
        );
    }
    if ((action.kind === "schedule" || action.kind === "reschedule") && onSetSchedule) {
        return (
            <Button size="sm" variant="outline" className="h-7 shrink-0 bg-transparent text-xs" onClick={onSetSchedule}>
                {action.label}
            </Button>
        );
    }
    if (action.kind === "add_devices" && onAddDevices) {
        return (
            <Button size="sm" variant="outline" className="h-7 shrink-0 bg-transparent text-xs" onClick={onAddDevices}>
                {action.label}
            </Button>
        );
    }
    // #1545 — opens the resend dialog seeded with ONLY the people the last
    // send missed, so clearing this row can't re-blast everyone who already
    // received the packet.
    if (action.kind === "resend_missed" && onResendMissed) {
        return (
            <Button size="sm" className="h-7 shrink-0 text-xs" onClick={onResendMissed}>
                ↻ {action.label}
            </Button>
        );
    }
    if (action.kind === "help_desk") {
        return (
            <Button size="sm" variant="outline" className="h-7 shrink-0 bg-transparent text-xs" onClick={openHelpDesk}>
                {action.label}
            </Button>
        );
    }
    // "resume" has no button here — the header already offers Resume, so we
    // don't duplicate a status flip inside this read-only panel.
    return null;
}

// ── Story row (derived beats) ──────────────────────────────────────────────

function StoryRow({ story }: { story: JobStory }) {
    if (!story.happened) {
        return <span className="text-muted-foreground">{story.preVisitLine}</span>;
    }
    return (
        <span className="leading-relaxed">
            {story.head && <b>{story.head}</b>}
            {story.beats.map((b) => (
                <span key={b.assetId}>
                    {" · "}
                    <span
                        className={cn(
                            "font-semibold",
                            b.outcome === "passed"
                                ? "text-emerald-600 dark:text-emerald-400"
                                : b.outcome === "failed"
                                  ? "text-red-600 dark:text-red-400"
                                  : "text-muted-foreground",
                        )}
                    >
                        {OUTCOME_MARK[b.outcome]} {b.label} {OUTCOME_WORD[b.outcome]}
                    </span>
                    {b.reason && <span className="text-foreground/70"> ({b.reason})</span>}
                </span>
            ))}
            {story.money.length > 0 && (
                <span>
                    {" → "}
                    {story.money.map((m, i) => (
                        <span key={m.id}>
                            {i > 0 && " · "}
                            <b>
                                {m.amount ? `${m.amount} ` : ""}
                                {m.kind === "invoice" ? "invoice" : "quote"}
                            </b>{" "}
                            <span className="text-foreground/70">{m.status}</span>
                        </span>
                    ))}
                </span>
            )}
            {story.delivery && (
                <span data-testid="story-delivered-another-way">
                    {" · "}
                    <b>{story.delivery}</b>
                </span>
            )}
        </span>
    );
}

// ── The shared list ────────────────────────────────────────────────────────

interface JobPoResponse {
    orders: BlockerPoLike[];
}

/**
 * Everything in the way of this job, as ONE list: check-up rows first (they
 * stop the packet going out), then the derived blockers. The NOW panel renders
 * it and the check-up strip above counts it, so the two can never disagree
 * ("4 things in the way" over 3 rows, "One thing to fix" over 2 — audit
 * 2026-10-01). Both callers share the PO and check-up query keys, so the
 * second call is a cache read. `ready` is false while an office user's
 * fetches are in flight — never render "on track" before then.
 */
export function useJobNeeds({
    job,
    isOffice,
    deviceCount,
    invoices,
    packetMissedOnLastSend = null,
}: {
    job: JobDetail | null | undefined;
    isOffice: boolean;
    deviceCount: number | null;
    invoices: LinkedInvoice[];
    packetMissedOnLastSend?: number | null;
}): { ready: boolean; blockers: JobBlocker[]; onTrack: string } {
    // Same PO query key the PO card uses, so the two share one fetch/cache
    // entry. Office-only route — non-office users derive from job + invoices.
    const poQuery = useQuery({
        queryKey: ["job-purchase-orders", job?.id],
        queryFn: () => api.get<JobPoResponse>(`/api/purchase-orders/for-job/${job?.id}`),
        enabled: isOffice && !!job,
        staleTime: 30_000,
    });
    // #1669 — the Help Desk's check-up (paperwork, filings, held packet).
    const checkupQ = useJobCheckup(job?.id, isOffice);

    if (!job || (isOffice && (poQuery.isLoading || checkupQ.isLoading))) {
        return { ready: false, blockers: [], onTrack: "" };
    }
    const jobLike: BlockerJobLike = job;
    const invoiceLike: BlockerInvoiceLike[] = invoices;
    const derived = deriveJobBlockers(
        jobLike,
        poQuery.data?.orders ?? [],
        invoiceLike,
        deviceCount,
        packetMissedOnLastSend,
    );
    return {
        ready: true,
        blockers: [...blockersFromCheckup(checkupQ.data?.problems ?? [], derived.blockers), ...derived.blockers],
        onTrack: derived.onTrack,
    };
}

// ── The panel ──────────────────────────────────────────────────────────────

export function NowPanel({
    job,
    isOffice,
    deviceCount,
    invoices,
    estimates,
    testResults,
    primaryAction,
    onRunAction,
    onOpenPaperwork,
    onSendInvoice,
    onSetSchedule,
    onAddDevices,
    onResendMissed,
    packetMissedOnLastSend = null,
    summarySlot,
    finishLineMode = false,
    deliveredAnotherWay = null,
}: {
    job: JobDetail;
    isOffice: boolean;
    deviceCount: number | null;
    invoices: LinkedInvoice[];
    estimates: LinkedEstimate[];
    testResults: JobTestResult[];
    /** ONE stage-derived primary (primaryHeaderActions(...).primary[0]). */
    primaryAction: HeaderAction | null;
    onRunAction: (action: HeaderAction) => void;
    /** Money stats scroll to their Paperwork rows. */
    onOpenPaperwork: () => void;
    onSendInvoice?: (invoiceId: string) => void;
    onSetSchedule?: () => void;
    onAddDevices?: () => void;
    /** #1545 — opens the resend dialog seeded with just the misses. */
    onResendMissed?: () => void;
    /** #1545 — how many recipients the last packet send failed to reach. Null
     *  while the send history is still loading, which must read as "don't
     *  know" rather than "nobody missed". */
    packetMissedOnLastSend?: number | null;
    /** Legacy slot for the summary. The job page now passes null: the scope
     *  lives in "What the tech will do" above, which "On track" never folds. */
    summarySlot?: React.ReactNode;
    /** #1714 — the Finish Line is drawn above and owns "what's next": this
     *  card drops its story, "What needs you" and "Next step" zones and keeps
     *  the ask, the pinned tech note and the money. */
    finishLineMode?: boolean;
    /** The packet was recorded as delivered without email (2026-10-07). */
    deliveredAnotherWay?: DeliveredAnotherWay | null;
}) {
    const [expanded, setExpanded] = useState(false);

    const story = deriveJobStory(job, testResults, invoices, estimates, deliveredAnotherWay);

    // Every row, uncapped — the same list the check-up strip counts.
    const needs = useJobNeeds({ job, isOffice, deviceCount, invoices, packetMissedOnLastSend });

    // Wait for the PO fetch and the check-up (office only) so we never flash
    // "on track" before a blocker resolves. (Finish Line mode shows no
    // blockers, so it doesn't wait.)
    if (!needs.ready && !finishLineMode) return null;

    const blockers = needs.blockers;
    // A NEXT STEP the parts gate would bounce ("Schedule" beside "Waiting on
    // parts") is dropped; the parts row's own button is the next step.
    const nextStep = primaryAction && !nextStepContradicted(primaryAction, blockers) ? primaryAction : null;

    const jobOpen = !["completed", "closed", "cancelled"].includes(job.status);
    const techNote = jobOpen && story.happened ? job.notes || job.tech_instructions : null;

    const totals = deriveMoneyTotals(invoices, estimates);
    const showMoney = story.money.length > 0;

    const moneyZone = showMoney && (
        <div className="min-w-[200px] flex-1 basis-52" data-testid="now-money">
            <h3 className="mb-1 text-[10px] font-extrabold uppercase tracking-widest text-muted-foreground">Money</h3>
            {story.money.map((m) => (
                <button
                    key={m.id}
                    type="button"
                    className="flex w-full items-baseline justify-between gap-3 py-0.5 text-left text-sm tabular-nums hover:text-primary"
                    title="Jump to its Paperwork row"
                    onClick={onOpenPaperwork}
                >
                    <span className="truncate">{m.label}</span>
                    <span className="shrink-0">
                        <b>{m.amount ?? "—"}</b> <span className="text-xs text-muted-foreground">{m.status}</span>
                    </span>
                </button>
            ))}
            <div
                className="flex items-baseline justify-between gap-3 border-t py-0.5 text-sm tabular-nums"
                title="Not yet collected: open invoice balance + quotes awaiting a yes"
            >
                <span>Balance</span>
                <b>{formatMoneyStat(totals.balance)}</b>
            </div>
        </div>
    );

    const techNoteBlock = techNote && (
        <div
            className="flex items-start gap-2 rounded-md border border-amber-300/60 bg-amber-50 p-2.5 text-xs dark:border-amber-500/30 dark:bg-amber-500/10"
            data-testid="now-technote"
        >
            <Pin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600 dark:text-amber-400" />
            <div className="min-w-0">
                <span className="font-semibold text-amber-800 dark:text-amber-300">
                    Tech note
                    {job.assigned_to_name && (
                        <span className="font-normal text-amber-700/90 dark:text-amber-300/80">
                            {" "}
                            · {job.assigned_to_name}
                        </span>
                    )}
                </span>
                <p className="mt-0.5 whitespace-pre-wrap text-amber-900/90 dark:text-amber-100/90">{techNote}</p>
            </div>
        </div>
    );

    // ── Finish Line mode (#1714): the ask, the tech note, the money. ──
    if (finishLineMode) {
        return (
            <Card data-testid="now-panel-finish-line">
                <CardContent className="space-y-2.5 p-4">
                    {summarySlot && <div data-testid="now-summary">{summarySlot}</div>}
                    {techNoteBlock}
                    {moneyZone && <div className="flex flex-wrap gap-x-8 gap-y-3 border-t pt-2.5">{moneyZone}</div>}
                </CardContent>
            </Card>
        );
    }

    // ── On-track: the whole card is one green line (details a click away) ──
    if (blockers.length === 0 && !expanded) {
        return (
            <Card className="border-l-4 border-l-emerald-500 dark:border-l-emerald-500/70" data-testid="now-ontrack">
                <CardContent className="flex items-center gap-2 px-4 py-2.5 text-sm">
                    <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
                    <span className="min-w-0 truncate">
                        <span className="font-semibold text-emerald-700 dark:text-emerald-400">{needs.onTrack}</span>
                        <span className="ml-2 text-muted-foreground">{story.line ?? story.preVisitLine}</span>
                    </span>
                    <Button
                        size="sm"
                        variant="ghost"
                        className="ml-auto h-7 shrink-0 gap-1 text-xs text-muted-foreground"
                        onClick={() => setExpanded(true)}
                    >
                        Details <ChevronDown className="h-3.5 w-3.5" />
                    </Button>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card
            className={cn(
                "border-l-4",
                blockers.length > 0
                    ? "border-l-red-500 dark:border-l-red-500/70"
                    : "border-l-emerald-500 dark:border-l-emerald-500/70",
            )}
            data-testid="now-panel"
        >
            <CardContent className="space-y-2.5 p-4">
                {/* SUMMARY — the ask (dims once the story takes the lead) */}
                {summarySlot && <div data-testid="now-summary">{summarySlot}</div>}

                {/* THE STORY — what happened (derived, never typed) */}
                <div className="border-t pt-2 text-sm" data-testid="now-story">
                    <span className="mr-2 align-middle text-[10px] font-extrabold uppercase tracking-widest text-muted-foreground">
                        The story — what happened
                    </span>
                    <StoryRow story={story} />
                </div>

                {/* Pinned tech note — the field's voice, up top while it matters */}
                {techNoteBlock}

                {/* Zones: what needs you · next step · money */}
                <div className="flex flex-wrap items-stretch gap-x-8 gap-y-3 border-t pt-2.5">
                    <div className="min-w-[220px] flex-1 basis-64" data-testid="now-needs">
                        <h3 className="mb-1 text-[10px] font-extrabold uppercase tracking-widest text-muted-foreground">
                            What needs you
                        </h3>
                        {blockers.length === 0 ? (
                            <div className="flex items-center gap-2 py-1 text-sm text-muted-foreground">
                                <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
                                <span>{needs.onTrack}</span>
                            </div>
                        ) : (
                            blockers.map((b) => (
                                <div
                                    key={b.id}
                                    className="flex items-start gap-2 py-1 text-sm"
                                    data-testid={`blocker-${b.id}`}
                                >
                                    <span
                                        className={cn("mt-1.5 h-2 w-2 shrink-0 rounded-full", SEVERITY_DOT[b.severity])}
                                        aria-hidden
                                    />
                                    <BlockerIcon blocker={b} className="mt-0.5 h-4 w-4 shrink-0 text-foreground/60" />
                                    <div className="min-w-0 flex-1 leading-snug">
                                        <span className="font-semibold text-foreground">{b.title}</span>
                                        <span className="text-foreground/75"> — {b.detail}</span>
                                    </div>
                                    <BlockerActionButton
                                        blocker={b}
                                        onSendInvoice={onSendInvoice}
                                        onSetSchedule={onSetSchedule}
                                        onAddDevices={onAddDevices}
                                        onResendMissed={onResendMissed}
                                    />
                                </div>
                            ))
                        )}
                    </div>

                    {nextStep && (
                        <div
                            className="flex min-w-[180px] flex-col items-center justify-center gap-1"
                            data-testid="now-next"
                        >
                            <h3 className="text-[10px] font-extrabold uppercase tracking-widest text-muted-foreground">
                                Next step
                            </h3>
                            {nextStep.kind === "link" ? (
                                <Button asChild size="sm">
                                    <Link to={nextStep.to}>{nextStep.label}</Link>
                                </Button>
                            ) : (
                                <Button size="sm" onClick={() => onRunAction(nextStep)}>
                                    {nextStep.label}
                                </Button>
                            )}
                        </div>
                    )}

                    {moneyZone}
                </div>

                {blockers.length === 0 && (
                    <div className="flex justify-end">
                        <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 gap-1 text-xs text-muted-foreground"
                            onClick={() => setExpanded(false)}
                        >
                            Collapse <ChevronUp className="h-3.5 w-3.5" />
                        </Button>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
